import { Link, useLocation } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileDown } from "lucide-react";

// Set to true to surface the CV download in the header.
const SHOW_CV_BUTTON = false;

const Navigation = () => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-medium ease-standard ${
        scrolled ?
        "bg-background/95 backdrop-blur-md border-b border-border shadow-sm" :
        "bg-background border-b border-border"}`
        }>
        
        {/* Top accent line */}
        <div className="h-0.5 bg-accent w-full" />

        <div className="flex items-stretch justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="border-r border-border px-4 py-2 md:px-5 md:py-2.5 flex items-center gap-2.5 group hover:bg-muted transition-colors duration-medium">
            
            <motion.div
              className="w-7 h-7 bg-foreground text-background flex items-center justify-center relative overflow-hidden rounded"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}>
              
              <span className="font-display text-sm relative z-10">A.</span>
            </motion.div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-sm tracking-tight">ARJAN&nbsp; Sethi</span>
              <span className="hidden md:inline font-mono text-[9px] text-muted-foreground tracking-widest">
                PRODUCT, DESIGN & INTELLIGENT SYSTEMS
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-stretch flex-1">
            <Link
              to="/"
              className={`flex items-center justify-center border-r border-border px-5 py-2.5 relative group overflow-hidden transition-colors duration-medium ${
              isActive("/") ?
              "bg-foreground text-background" :
              "hover:bg-muted"}`
              }>
              
              <span className="font-mono text-[10px] text-accent mr-2">01</span>
              <span className="font-display text-xs uppercase tracking-[0.2em] relative z-10">
                Works
              </span>
              <div className="absolute bottom-0 left-0 h-px bg-accent w-0 group-hover:w-full transition-all duration-medium" />
            </Link>
            <a
              href="/#philosophy"
              className="flex items-center justify-center border-r border-border px-5 py-2.5 relative group overflow-hidden transition-colors duration-medium hover:bg-muted">
              
              <span className="font-mono text-[10px] text-accent mr-2">02</span>
              <span className="font-display text-xs uppercase tracking-[0.2em] relative z-10">
                About
              </span>
              <div className="absolute bottom-0 left-0 h-px bg-accent w-0 group-hover:w-full transition-all duration-medium" />
            </a>
            <Link
              to="/thinking"
              className={`flex items-center justify-center border-r border-border px-5 py-2.5 relative group overflow-hidden transition-colors duration-medium ${
              isActive("/thinking") ?
              "bg-foreground text-background" :
              "hover:bg-muted"}`
              }>
              
              <span className="font-mono text-[10px] text-accent mr-2">03</span>
              <span className="font-display text-xs uppercase tracking-[0.2em] relative z-10">
                Thinking
              </span>
              <div className="absolute bottom-0 left-0 h-px bg-accent w-0 group-hover:w-full transition-all duration-medium" />
            </Link>
            <a
              href="mailto:arjansethi@gmail.com"
              className="flex items-center justify-center border-r border-border px-5 py-2.5 relative group overflow-hidden transition-colors duration-medium hover:bg-muted">
              
              <span className="font-mono text-[10px] text-accent mr-2">04</span>
              <span className="font-display text-xs uppercase tracking-[0.2em] relative z-10">
                Contact
              </span>
              <div className="absolute bottom-0 left-0 h-px bg-accent w-0 group-hover:w-full transition-all duration-medium" />
            </a>
          </div>

          {/* Right section */}
          <div className="flex items-stretch">
            {/* Status indicator - desktop only */}
            




            

            {/* CV Download button - desktop only (hidden) */}
            {SHOW_CV_BUTTON && (
              <a
                href="/Arjan_Sethi_CV.pdf"
                download
                className="hidden lg:flex items-center gap-1.5 border-l border-border px-4 py-2.5 text-foreground hover:bg-muted transition-colors duration-medium group">
                
                <FileDown className="w-3 h-3" />
                <span className="font-mono text-[9px] uppercase tracking-widest">
                  CV
                </span>
              </a>
            )}

            {/* Theme toggle - desktop */}
            <div className="hidden md:flex items-center justify-center px-3 py-2.5 border-l border-border hover:bg-muted transition-colors duration-medium">
              <ThemeToggle />
            </div>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="ml-auto md:ml-0 px-4 py-2.5 hover:bg-muted transition-colors duration-medium flex items-center gap-2 group min-h-[40px] border-l border-border"
              aria-label="Toggle menu">
              
              <span className="hidden sm:block font-mono text-[9px] uppercase tracking-widest">
                {mobileMenuOpen ? "Close" : "Menu"}
              </span>
              <div className="flex flex-col justify-center items-center gap-1 w-4 h-4">
                <span
                  className={`block h-px bg-current transition-all duration-medium ${
                  mobileMenuOpen ? "w-4 rotate-45 translate-y-[5px]" : "w-4"}`
                  } />
                
                <span
                  className={`block h-px bg-current transition-all duration-medium ${
                  mobileMenuOpen ? "opacity-0 w-0" : "w-2.5"}`
                  } />
                
                <span
                  className={`block h-px bg-current transition-all duration-medium ${
                  mobileMenuOpen ? "w-4 -rotate-45 -translate-y-[5px]" : "w-4"}`
                  } />
                
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-background transition-all duration-slow ${
        mobileMenuOpen ?
        "opacity-100 pointer-events-auto" :
        "opacity-0 pointer-events-none"}`
        }
        style={{ paddingTop: "calc(1rem + 57px)" }}>
        
        {/* Grid background pattern */}
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="relative h-full flex flex-col">
          {/* Main nav items */}
          <div className="flex-1 flex flex-col justify-center px-6 md:px-12">
            <div className="space-y-2">
              {[
              { label: "Works", path: "/", num: "01" },
              { label: "About", path: "/#philosophy", num: "02", isExternal: true },
              { label: "Thinking", path: "/thinking", num: "03" },
              { label: "Contact", path: "mailto:arjansethi@gmail.com", num: "04", isExternal: true }].
              map((item, index) =>
              <motion.div
                key={item.label}
                initial={{ x: -30, opacity: 0 }}
                animate={
                mobileMenuOpen ?
                { x: 0, opacity: 1 } :
                { x: -30, opacity: 0 }
                }
                transition={{ delay: index * 0.1, duration: 0.4 }}>
                
                  {item.isExternal ?
                <a
                  href={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="group flex items-baseline gap-4 py-4 border-b border-border hover:border-accent transition-colors duration-medium rounded-lg hover:bg-muted px-4 -mx-4 min-h-[44px]">
                  
                      <span className="font-mono text-xs text-accent">
                        {item.num}
                      </span>
                      <span className="font-display text-[clamp(2.5rem,10vw,6rem)] uppercase leading-none group-hover:text-accent transition-colors duration-medium">
                        {item.label}
                      </span>
                    </a> :

                <Link
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`group flex items-baseline gap-4 py-4 border-b border-border hover:border-accent transition-colors duration-medium rounded-lg hover:bg-muted px-4 -mx-4 min-h-[44px] ${
                  isActive(item.path) ? "border-accent bg-muted" : ""}`
                  }>
                  
                      <span className="font-mono text-xs text-accent">
                        {item.num}
                      </span>
                      <span
                    className={`font-display text-[clamp(2.5rem,10vw,6rem)] uppercase leading-none transition-colors duration-medium ${
                    isActive(item.path) ?
                    "text-accent" :
                    "group-hover:text-accent"}`
                    }>
                    
                        {item.label}
                      </span>
                    </Link>
                }
                </motion.div>
              )}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-border">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
              <div className="px-6 py-4 flex items-center gap-2 min-h-[44px]">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                  Available for work
                </span>
              </div>
              <div className="px-6 py-4 flex items-center justify-end md:justify-start min-h-[44px]">
                <ThemeToggle />
              </div>
              <div className="hidden md:flex px-6 py-4 items-center min-h-[44px]">
                <span className="font-mono text-xs text-muted-foreground">
                  © {new Date().getFullYear()}
                </span>
              </div>
              <div className="hidden md:flex px-6 py-4 items-center justify-end gap-6 min-h-[44px]">
                <a
                  href="https://linkedin.com/in/arjansethi"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Arjan Sethi on LinkedIn"
                  className="font-mono text-xs uppercase tracking-wider hover:text-accent transition-colors duration-medium">
                  
                  Li
                </a>
                <a
                  href="https://x.com/arjansethi"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Arjan Sethi on X"
                  className="font-mono text-xs uppercase tracking-wider hover:text-accent transition-colors duration-medium">
                  
                  X
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default Navigation;